import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import {
  type Bdkv2BookDescriptor,
  type V2BookConnectionResponse,
  type LibraryServiceAddWorkspaceBookConnectionBody,
  type LibraryServiceUpdateWorkspaceBookConnectionBody,
  type V2BookConnectionDescriptor,
  type Bdkv2Value,
  type Bdkv2Label,
  Bdkv2OAuthFlow,
} from '@/generated/hooks/bdk/models';
import {
  useAddWorkspaceBookConnection,
  useAuthorizeWorkspaceBookConnection,
  useUpdateWorkspaceBookConnection,
  getRetrieveAllWorkspaceBookConnectionsQueryKey,
  getRetrieveWorkspaceBookConnectionsQueryKey,
  getRetrieveWorkspaceBookConnectionDiscoverablesQueryKey,
} from '@/generated/hooks/bdk/v2/services/library';
import { useDeleteWorkspaceConnection } from '@/generated/hooks/grimoire/v1/api';
import {
  findKeyValueAuthentication,
  getKeyValueCredentialIds,
  isOAuthConnection,
  isSensitiveCredential,
  REDACTED_CREDENTIAL_VALUE,
} from '@/modules/integrations/utils';
import { captureException } from '@/modules/sentry';
import {
  reauthorizeCredential,
  unmarkManagedOAuthReturn,
  withoutManagedOAuthMarker,
  writePendingOAuthConnection,
} from '@/modules/credentials';
import { getRoutePath } from '@/routes/util';
import { validateOAuthRedirectUrl } from '@/shared/utils';
import { useWaitForConnectionToBeReady } from './useWaitForConnectionToBeReady';

const CREDENTIAL_KEYS_LABEL_PREFIX = 'credential_keys_';

function extractCredentialKeysLabels(
  credentials: Record<string, string>,
  keyValueCredentialIds: string[],
): Bdkv2Label[] {
  const labels: Bdkv2Label[] = [];
  for (const [credId, value] of Object.entries(credentials)) {
    // Only extract key labels for key/value credentials — not arbitrary
    // credential values that happen to be valid JSON (e.g. a JSON config
    // string used as an API key in a non-browser integration).
    if (!keyValueCredentialIds.includes(credId)) continue;
    try {
      const obj = JSON.parse(value);
      if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
        labels.push({
          key: `${CREDENTIAL_KEYS_LABEL_PREFIX}${credId}`,
          value: Object.keys(obj).join('.'),
        });
      }
    } catch {
      // not valid JSON, skip
    }
  }
  return labels;
}

export const useConnectionActions = (
  organizationId: string,
  workspaceId: string,
  integrationName?: string,
  integrationVersion?: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('integrations');
  const waitForConnectionToBeReady = useWaitForConnectionToBeReady();

  // Build dynamic referer URL for OAuth callback
  const getRefererUrl = () => {
    if (integrationName && integrationVersion) {
      const path = getRoutePath('INTEGRATIONS_INTEGRATION_DETAILS', {
        orgId: organizationId,
        workspaceId,
        integrationName,
        integrationVersion,
      });
      return `${window.location.origin}${path}`;
    }
    return withoutManagedOAuthMarker(window.location.href);
  };

  const {
    mutateAsync: addIntegrationConnection,
    isPending: isAddingIntegrationConnection,
  } = useAddWorkspaceBookConnection({
    axios: {
      headers: {
        // Any state I add here, I'll be returned in the url. Use state from here to go ahead with the flow
        // This is required for the OAuth flow to work.
        referer: getRefererUrl(),
        'X-Forwarded-Host': `${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`,
        'X-Forwarded-Proto': window.location.protocol.slice(0, -1), // Remove trailing ':'
      },
    },
  });
  const {
    mutateAsync: updateIntegrationConnection,
    isPending: isUpdatingIntegrationConnection,
  } = useUpdateWorkspaceBookConnection();
  const {
    mutateAsync: authorizeWorkspaceBookConnection,
    isPending: isReconnecting,
  } = useAuthorizeWorkspaceBookConnection({
    axios: {
      headers: {
        referer: getRefererUrl(),
        'X-Forwarded-Host': `${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`,
        'X-Forwarded-Proto': window.location.protocol.slice(0, -1),
      },
    },
  });
  const { mutateAsync: deleteIntegrationConnection } =
    useDeleteWorkspaceConnection();

  // Helper function to map credentials based on their type
  const mapCredentialsToApiFormat = (
    config: Record<string, string>,
    integration: Bdkv2BookDescriptor,
    authMethodId: string,
    oauthFlow?: Bdkv2OAuthFlow,
  ) => {
    if (oauthFlow) {
      // For OAuth, treat client_secret as sensitive, others as regular text
      return Object.keys(config).map(key => ({
        id: key,
        value:
          key === 'client_secret'
            ? {
                sensitive_value: {
                  value: {
                    text_value: config[key],
                  },
                },
              }
            : {
                text_value: config[key],
              },
      }));
    } else {
      // For custom auth, get credentials from the book descriptor
      const selectedAuth = integration.authentications?.find(auth => {
        const authId = auth.custom?.id;
        return authId === authMethodId;
      });

      const credentials = selectedAuth?.custom?.credentials || [];

      return Object.keys(config).map(key => {
        const credential = credentials.find(cred => cred.id === key);
        const isSensitive = !!credential && isSensitiveCredential(credential);

        return {
          id: key,
          value: isSensitive
            ? {
                sensitive_value: {
                  value: {
                    text_value: config[key],
                  },
                },
              }
            : {
                text_value: config[key],
              },
        };
      });
    }
  };

  /**
   * Creates an OAuth authorization code connection and returns the authorization URL.
   *
   * @param integration - The book descriptor
   * @param authMethodId - Authentication method ID
   * @param connectionName - Name for the new connection
   * @param credentials - Authentication credentials (client_id, client_secret, etc.)
   * @param labels - Labels for the connection (e.g., ['draft', 'published'])
   * @param configMap - Optional config_map values already in API format (Bdkv2Value)
   * @returns The OAuth authorization URL to redirect to
   */
  const createOAuthAuthorizationConnection = async (
    integration: Bdkv2BookDescriptor,
    authMethodId: string,
    connectionName: string,
    credentials: Record<string, string>,
    labels: string[],
    configMap?: Record<string, Bdkv2Value>,
  ): Promise<string> => {
    const requestData: LibraryServiceAddWorkspaceBookConnectionBody = {
      labels: labels.map(label => ({
        key: `${label}_connection`,
        value: 'true',
      })),
      connection_name: connectionName,
      authentication: {
        // Use the actual authentication_id from the book descriptor
        authentication_id: authMethodId,
        authentication_credentials: mapCredentialsToApiFormat(
          credentials,
          integration,
          authMethodId,
          Bdkv2OAuthFlow.OAuthFlowAuthorizationCode,
        ),
      },
      oauth_flow: Bdkv2OAuthFlow.OAuthFlowAuthorizationCode,
    };

    // Include config_map if provided
    if (configMap && Object.keys(configMap).length > 0) {
      requestData.config_map = configMap;
    }

    const response = await addIntegrationConnection({
      organizationId,
      workspaceId,
      bookName: integration.name || '',
      bookVersion: integration.version || '',
      data: requestData,
    });

    // Extract authorize_url from response
    const authorizeUrl = response.data.authorize_url?.url;
    if (!authorizeUrl) {
      throw new Error('No authorize URL returned from server');
    }

    // Validate URL before returning to prevent open redirect vulnerabilities
    validateOAuthRedirectUrl(authorizeUrl);

    return authorizeUrl;
  };

  /**
   * Creates a new book connection with the provided credentials and optional config_map.
   *
   * @param integration - The book descriptor
   * @param connectionName - Name for the new connection
   * @param authMethodId - Authentication method ID
   * @param credentials - Authentication credentials
   * @param labels - Labels for the connection (e.g., ['draft', 'published'])
   * @param oauthFlow - OAuth flow type if using OAuth
   * @param configMap - Optional config_map values already in API format (Bdkv2Value)
   * @returns The created connection response
   */
  const createConnection = async (
    integration: Bdkv2BookDescriptor,
    connectionName: string,
    authMethodId: string,
    credentials: Record<string, string>,
    labels: string[],
    oauthFlow?: Bdkv2OAuthFlow,
    configMap?: Record<string, Bdkv2Value>,
    remoteConnectionId?: string,
  ): Promise<V2BookConnectionResponse> => {
    // Check if integration requires authentication
    const hasAuth = authMethodId && authMethodId !== '';

    // Build the request data
    const connectionLabels: Bdkv2Label[] = labels.map(label => ({
      key: `${label}_connection`,
      value: 'true',
    }));
    const credKeysLabels = extractCredentialKeysLabels(
      credentials,
      getKeyValueCredentialIds(integration, authMethodId),
    );

    const requestData: LibraryServiceAddWorkspaceBookConnectionBody = {
      labels: [...connectionLabels, ...credKeysLabels],
      connection_name: connectionName,
    };

    // Only include authentication field if the book requires it
    if (hasAuth && credentials) {
      requestData.authentication = {
        authentication_id: authMethodId,
        authentication_credentials: mapCredentialsToApiFormat(
          credentials,
          integration,
          authMethodId,
          oauthFlow,
        ),
      };
      // Include oauth_flow if provided (for OAuth methods)
      if (oauthFlow) {
        requestData.oauth_flow = oauthFlow;
      }
    }

    // Include config_map if provided
    if (configMap && Object.keys(configMap).length > 0) {
      requestData.config_map = configMap;
    }

    // Include remote connection ID if provided
    if (remoteConnectionId) {
      requestData.associated_remote_connection_id = remoteConnectionId;
    }

    const response = await addIntegrationConnection({
      organizationId,
      workspaceId,
      bookName: integration.name || '',
      bookVersion: integration.version || '',
      data: requestData,
    });

    // Invalidate all connection queries for this workspace
    queryClient.invalidateQueries({
      queryKey: getRetrieveAllWorkspaceBookConnectionsQueryKey(
        organizationId,
        workspaceId,
      ),
    });

    queryClient.invalidateQueries({
      queryKey: getRetrieveWorkspaceBookConnectionsQueryKey(
        organizationId,
        workspaceId,
        integration.name,
        integration.version,
      ),
    });

    return response.data;
  };

  /**
   * Updates an existing book connection with new credentials and optional config_map.
   *
   * @param connection - The connection descriptor to update
   * @param credentials - Updated authentication credentials
   * @param integration - The book descriptor
   * @param configMap - Optional config_map values already in API format (Bdkv2Value)
   * @returns The updated connection response
   */
  const updateConnection = async (
    connection: V2BookConnectionDescriptor,
    credentials: Record<string, string>,
    integration: Bdkv2BookDescriptor,
    configMap?: Record<string, Bdkv2Value>,
  ): Promise<V2BookConnectionResponse> => {
    const originalAuthId = connection.authentication?.authentication_id || '';

    let authMethodId = originalAuthId;

    // Resolve auth ID for no-auth connections that now have key/value credentials
    if (!authMethodId && Object.keys(credentials).length > 0) {
      authMethodId = findKeyValueAuthentication(integration)?.custom?.id || '';
    }
    const keyValueCredentialIds = getKeyValueCredentialIds(
      integration,
      authMethodId,
    );

    // The backend does not allow changing a connection's authentication_id via
    // update (gRPC UNIMPLEMENTED: "Cannot change authentication id, only
    // credential values"). When transitioning from no-auth to authenticated,
    // delete the old connection and create a new one with the correct auth.
    if (
      !originalAuthId &&
      authMethodId &&
      Object.keys(credentials).length > 0
    ) {
      // Preserve all existing labels except credential_keys_* (which get rebuilt
      // from the new credentials). The previous filter only kept *_connection
      // labels, silently dropping any other labels on the connection.
      const existingLabels = (connection.labels || []).filter(
        l => !l.key?.startsWith(CREDENTIAL_KEYS_LABEL_PREFIX),
      );

      await deleteIntegrationConnection({
        organization: organizationId,
        workspace: workspaceId,
        params: {
          connection_id: connection.connection_id!,
        },
      });

      const response = await addIntegrationConnection({
        organizationId,
        workspaceId,
        bookName: connection.book_name!,
        bookVersion: connection.book_version!,
        data: {
          connection_name: connection.connection_name,
          labels: [
            ...existingLabels,
            ...extractCredentialKeysLabels(credentials, keyValueCredentialIds),
          ],
          authentication: {
            authentication_id: authMethodId,
            authentication_credentials: mapCredentialsToApiFormat(
              credentials,
              integration,
              authMethodId,
              undefined,
            ),
          },
          config_map:
            configMap !== undefined ? configMap : connection.config_map,
        },
      });

      queryClient.invalidateQueries({
        queryKey: getRetrieveAllWorkspaceBookConnectionsQueryKey(
          organizationId,
          workspaceId,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: getRetrieveWorkspaceBookConnectionsQueryKey(
          organizationId,
          workspaceId,
          connection.book_name!,
          connection.book_version!,
        ),
      });

      // The old connection_id is gone, drop any cached discoverables for it.
      if (connection.connection_id) {
        queryClient.removeQueries({
          queryKey: getRetrieveWorkspaceBookConnectionDiscoverablesQueryKey(
            organizationId,
            workspaceId,
            connection.connection_id,
          ),
        });
      }

      // Once the new connection's deployment settles, invalidate any cached
      // discoverables so the next read hits the new book pod. Fire-and-forget
      // so the caller doesn't block while the rollout completes.
      // `invalidateQueries` (not `removeQueries`) so an already-mounted
      // ConfigureActionsDialog silently re-fetches in the background rather
      // than flashing back to a loading state.
      const newConnectionId =
        response.data.book_connection_descriptor?.connection_id;
      if (newConnectionId) {
        void waitForConnectionToBeReady(newConnectionId)
          .catch(() => undefined)
          .finally(() => {
            queryClient.invalidateQueries({
              queryKey: getRetrieveWorkspaceBookConnectionDiscoverablesQueryKey(
                organizationId,
                workspaceId,
                newConnectionId,
              ),
            });
          });
      }
      return response.data;
    }

    const authOAuthDescriptor = integration.authentications?.find(
      auth => auth.oauth?.id === authMethodId,
    );
    const oauthFlow = authOAuthDescriptor?.oauth?.flows?.[0];

    const credKeysLabels = extractCredentialKeysLabels(
      credentials,
      keyValueCredentialIds,
    );
    // A key/value credential sent back redacted keeps its stored value, so its
    // saved key names stay valid and its label is carried over unchanged.
    const keptKeysLabels = keyValueCredentialIds
      .filter(id => credentials[id] === REDACTED_CREDENTIAL_VALUE)
      .map(id => `${CREDENTIAL_KEYS_LABEL_PREFIX}${id}`);
    const existingLabels = (connection.labels || []).filter(
      l =>
        !l.key?.startsWith(CREDENTIAL_KEYS_LABEL_PREFIX) ||
        keptKeysLabels.includes(l.key),
    );

    // Omit authentication entirely for pure no-auth updates (no auth method
    // AND no credentials). The backend rejects any authentication object for
    // books that have no authentication defined, even with empty fields.
    // If credentials are present without an auth method we still send the
    // authentication block so data is not silently dropped.
    const isNoAuth = !authMethodId && Object.keys(credentials).length === 0;

    const requestData: LibraryServiceUpdateWorkspaceBookConnectionBody = {
      connection_name: connection.connection_name,
      labels: [...existingLabels, ...credKeysLabels],
      config_map: configMap !== undefined ? configMap : connection.config_map,
      ...(!isNoAuth
        ? {
            authentication: {
              ...connection.authentication,
              authentication_id: authMethodId,
              authentication_credentials: mapCredentialsToApiFormat(
                credentials,
                integration,
                authMethodId,
                oauthFlow,
              ),
            },
          }
        : {}),
    };

    const response = await updateIntegrationConnection({
      organizationId,
      workspaceId,
      bookName: connection.book_name!,
      bookVersion: connection.book_version!,
      connectionId: connection.connection_id!,
      data: requestData,
    });

    // Invalidate all connection queries for this workspace
    queryClient.invalidateQueries({
      queryKey: getRetrieveAllWorkspaceBookConnectionsQueryKey(
        organizationId,
        workspaceId,
      ),
    });

    // Invalidate the specific book connections query
    queryClient.invalidateQueries({
      queryKey: getRetrieveWorkspaceBookConnectionsQueryKey(
        organizationId,
        workspaceId,
        connection.book_name!,
        connection.book_version!,
      ),
    });

    // Once the deployment settles, invalidate the cached discoverables so
    // the next read hits the new book pod with the new config.
    // Fire-and-forget so the caller doesn't block while the rollout completes.
    // `invalidateQueries` (not `removeQueries`) so an already-mounted
    // ConfigureActionsDialog silently re-fetches in the background rather
    // than flashing back to a loading state.
    if (connection.connection_id) {
      const connectionId = connection.connection_id;
      void waitForConnectionToBeReady(connectionId)
        .catch(() => undefined)
        .finally(() => {
          queryClient.invalidateQueries({
            queryKey: getRetrieveWorkspaceBookConnectionDiscoverablesQueryKey(
              organizationId,
              workspaceId,
              connectionId,
            ),
          });
        });
    }

    return response.data;
  };

  /**
   * Rewrites a connection's labels, leaving its credentials untouched.
   *
   * Used by the "same credentials for Test and Production" promotion, where the
   * only thing that changes is which environments the connection serves.
   *
   * The update is a PUT with no field mask, and the server rejects it without
   * an `authentication` block ("authentication is required for book <name>
   * version <version>"), so the connection's own block is echoed back exactly
   * as received — sensitive values included, which the server hands out as the
   * literal `<redacted>` and reads back as "keep what you already have". That is
   * the same request the edit dialog makes for any credential the user did not
   * retype, which is why a rename does not clear a secret.
   *
   * Echoing the descriptor is deliberate: rebuilding the map through
   * {@link getConnectionInitialValues} would turn a key/value
   * credential into `{"key": ""}`, and those empty values *would* overwrite the
   * stored secret. A connection with no authentication of its own sends no
   * block at all, since the server rejects one for a book that defines no auth.
   *
   * @param connection - The connection to relabel
   * @param labels - The complete label set to persist, replacing the existing one
   * @returns The updated connection response
   */
  const updateConnectionLabels = async (
    connection: V2BookConnectionDescriptor,
    labels: Bdkv2Label[],
  ): Promise<V2BookConnectionResponse> => {
    const requestData: LibraryServiceUpdateWorkspaceBookConnectionBody = {
      connection_name: connection.connection_name,
      labels,
      config_map: connection.config_map,
      ...(connection.authentication?.authentication_id
        ? { authentication: connection.authentication }
        : {}),
    };

    const response = await updateIntegrationConnection({
      organizationId,
      workspaceId,
      bookName: connection.book_name!,
      bookVersion: connection.book_version!,
      connectionId: connection.connection_id!,
      data: requestData,
    });

    queryClient.invalidateQueries({
      queryKey: getRetrieveAllWorkspaceBookConnectionsQueryKey(
        organizationId,
        workspaceId,
      ),
    });
    queryClient.invalidateQueries({
      queryKey: getRetrieveWorkspaceBookConnectionsQueryKey(
        organizationId,
        workspaceId,
        connection.book_name!,
        connection.book_version!,
      ),
    });

    return response.data;
  };

  /**
   * Starts OAuth re-authorization for an existing OAuth authorization connection.
   *
   * Filters connections using `isOAuthConnection` (explicit OAuth auth IDs and
   * managed OAuth where the auth ID is a UUID not matching any custom auth).
   * When multiple connections are passed (e.g. draft and published), the first
   * matching connection is used. On success redirects the browser to the provider
   * authorize URL.
   *
   * @param connections - Connection descriptors to reconnect (typically one group)
   * @param integration - Optional book descriptor for accurate managed OAuth detection
   * @returns Promise that may not resolve if the browser navigates away
   */
  const handleReconnect = async (
    connections: V2BookConnectionDescriptor[],
    integration?: Bdkv2BookDescriptor,
  ) => {
    if (connections.length === 0) {
      return;
    }

    const oauthAuthCodeConnections = connections.filter(c =>
      isOAuthConnection(c, integration),
    );
    if (oauthAuthCodeConnections.length === 0) {
      return;
    }

    const target = oauthAuthCodeConnections[0];
    const connectionId = target.connection_id;
    if (!connectionId) {
      return;
    }

    const toastId = toast.loading(
      t('integrations.connections.reconnectLoading'),
    );

    // A connection backed by a managed credential signs that credential in
    // again; BDK's own flow would only update the connection.
    if (target.credentials_id) {
      try {
        const { authorizeUrl } = await reauthorizeCredential(
          organizationId,
          workspaceId,
          target.credentials_id,
        );
        toast.dismiss(toastId);
        if (!authorizeUrl) {
          toast.success(t('integrations.connections.reconnectSuccess'));
          return;
        }
        validateOAuthRedirectUrl(authorizeUrl);
        writePendingOAuthConnection({
          organizationId,
          workspaceId,
          integrationName: target.book_name || '',
          integrationVersion: target.book_version || '',
          connectionName: target.connection_name || '',
          authMethodId: target.authentication?.authentication_id || '',
          credentialId: target.credentials_id,
          reconnect: true,
        });
        window.location.assign(authorizeUrl);
      } catch (error) {
        unmarkManagedOAuthReturn();
        toast.dismiss(toastId);
        captureException(
          error instanceof Error ? error : new Error(String(error)),
          { tags: { module: 'integrations', action: 'reauthorizeCredential' } },
        );
        toast.error(t('integrations.connections.reconnectError'));
      }
      return;
    }

    try {
      const response = await authorizeWorkspaceBookConnection({
        organizationId,
        workspaceId,
        connectionId,
      });

      const authorizeUrl = response.data.authorize_url?.url;
      if (!authorizeUrl) {
        throw new Error('No authorize URL returned from server');
      }
      validateOAuthRedirectUrl(authorizeUrl);

      queryClient.invalidateQueries({
        queryKey: getRetrieveAllWorkspaceBookConnectionsQueryKey(
          organizationId,
          workspaceId,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: getRetrieveWorkspaceBookConnectionsQueryKey(
          organizationId,
          workspaceId,
          target.book_name,
          target.book_version,
        ),
      });

      toast.dismiss(toastId);

      queueMicrotask(() => {
        window.location.assign(authorizeUrl);
      });
    } catch (error) {
      toast.dismiss(toastId);
      captureException(
        error instanceof Error ? error : new Error(String(error)),
        {
          tags: {
            module: 'integrations',
            action: 'handleReconnect',
          },
        },
      );
      toast.error(t('integrations.connections.reconnectError'));
    }
  };

  /**
   * Starts OAuth re-authorization for a connection identified only by its ID.
   *
   * Unlike `handleReconnect`, this function skips the `isOAuthConnection` filter
   * and calls `authorizeWorkspaceBookConnection` directly. Used by the upgrade
   * flow after grimoire has already migrated the connection to the target version,
   * so the provider issues a fresh token with the new scope.
   */
  const reconnectById = async (connectionId: string) => {
    const toastId = toast.loading(
      t('integrations.connections.reconnectLoading'),
    );
    try {
      const response = await authorizeWorkspaceBookConnection({
        organizationId,
        workspaceId,
        connectionId,
      });
      const authorizeUrl = response.data.authorize_url?.url;
      if (!authorizeUrl) {
        throw new Error('No authorize URL returned from server');
      }
      validateOAuthRedirectUrl(authorizeUrl);
      toast.dismiss(toastId);
      queueMicrotask(() => {
        window.location.assign(authorizeUrl);
      });
    } catch (error) {
      toast.dismiss(toastId);
      captureException(
        error instanceof Error ? error : new Error(String(error)),
        { tags: { module: 'integrations', action: 'reconnectById' } },
      );
      toast.error(t('integrations.connections.reconnectError'));
      throw error;
    }
  };

  return {
    createConnection,
    createOAuthAuthorizationConnection,
    isAddingIntegrationConnection,
    isReconnecting,
    isUpdatingIntegrationConnection,
    updateConnection,
    updateConnectionLabels,
    handleReconnect,
    reconnectById,
  };
};
